export const AI_ORDER_DIETARY_MAX_CHARS = 2000;

export const ALLERGY_CHIP_OPTIONS = [
  { id: "peanuts", label: "Peanuts" },
  { id: "tree_nuts", label: "Tree nuts" },
  { id: "dairy", label: "Dairy" },
  { id: "gluten", label: "Gluten" },
  { id: "shellfish", label: "Shellfish" },
  { id: "eggs", label: "Eggs" },
  { id: "soy", label: "Soy" },
  { id: "other", label: "Other" },
] as const;

export const DIET_CHIP_OPTIONS = [
  { id: "vegetarian", label: "Vegetarian" },
  { id: "vegan", label: "Vegan" },
  { id: "halal", label: "Halal" },
  { id: "kosher", label: "Kosher" },
  { id: "other", label: "Other" },
] as const;

export type AllergyChipId = (typeof ALLERGY_CHIP_OPTIONS)[number]["id"];
export type DietChipId = (typeof DIET_CHIP_OPTIONS)[number]["id"];

export type SpecialPassengerDraft = {
  id: string;
  label: string;
  /** @deprecated Legacy single-row kind; prefer adultCount / childCount */
  passengerKind?: "adult" | "child";
  /** Passengers (adults) sharing this allergy/diet profile */
  adultCount: number;
  /** Passengers (children) sharing this allergy/diet profile */
  childCount: number;
  allergyIds: string[];
  dietIds: string[];
  allergyOther: string;
  dietOther: string;
  notes: string;
};

export type PassengerServiceStyleId = "bulk" | "ready_to_serve" | "deconstructed" | "family_style";

/** Titles + full descriptions (shown in tooltip on the help control). */
export const PASSENGER_SERVICE_STYLE_OPTIONS: ReadonlyArray<{
  id: PassengerServiceStyleId;
  title: string;
  description: string;
}> = [
  {
    id: "bulk",
    title: "Bulk",
    description:
      "Food delivered in large quantities, packaged for the CFA to portion onboard based on passenger count.",
  },
  {
    id: "ready_to_serve",
    title: "Ready-to-serve",
    description:
      "Individually plated by the caterer and ready to present. No onboard preparation required from the CFA.",
  },
  {
    id: "deconstructed",
    title: "Deconstructed",
    description:
      "Components delivered separately (protein, sides, sauce, dressing). The CFA assembles onboard based on service timing and passenger preferences.",
  },
  {
    id: "family_style",
    title: "Family style",
    description:
      "Dishes presented on shared platters at the center of the table for passengers to serve themselves. Convivial format, typical of long flights or group travel.",
  },
];

export type MenuPreferencesState = {
  menuStyle: "light_fresh" | "classic_refined" | "comfort_generous" | null;
  cuisine: "french" | "italian" | "japanese" | "mediterranean" | "mix" | null;
  occasion: "none" | "birthday" | "celebration" | "business" | null;
  service: PassengerServiceStyleId | null;
};

const allergyLabel = (id: string) =>
  ALLERGY_CHIP_OPTIONS.find((o) => o.id === id)?.label ?? id;
const dietLabel = (id: string) => DIET_CHIP_OPTIONS.find((o) => o.id === id)?.label ?? id;

function labelsFromIds(ids: string[], kind: "allergy" | "diet"): string {
  const fn = kind === "allergy" ? allergyLabel : dietLabel;
  return ids.map(fn).filter(Boolean).join(", ");
}

export const MENU_STYLE_LABELS: Record<NonNullable<MenuPreferencesState["menuStyle"]>, string> = {
  light_fresh: "Light & fresh",
  classic_refined: "Classic & refined",
  comfort_generous: "Comfort & generous",
};

export const CUISINE_LABELS: Record<NonNullable<MenuPreferencesState["cuisine"]>, string> = {
  french: "French",
  italian: "Italian",
  japanese: "Japanese",
  mediterranean: "Mediterranean",
  mix: "Mix",
};

export const OCCASION_LABELS: Record<NonNullable<MenuPreferencesState["occasion"]>, string> = {
  none: "None",
  birthday: "Birthday",
  celebration: "Celebration",
  business: "Business meeting",
};

export const SERVICE_LABELS: Record<NonNullable<MenuPreferencesState["service"]>, string> = {
  bulk: "Bulk",
  ready_to_serve: "Ready-to-serve",
  deconstructed: "Deconstructed",
  family_style: "Family style",
};

export type BuildWizardDietaryInput = {
  preflightNotes: string;
  /** Optional flight leg for meal timing / duration inference (English, for the model). */
  flightLegContext: string | null;
  groupAllergyIds: string[];
  groupDietIds: string[];
  groupAllergyOther: string;
  groupDietOther: string;
  groupPreferences: string;
  specialPassengers: SpecialPassengerDraft[];
  /** When true, ask the model to account for crew meals. */
  includeCrewMeals: boolean;
  menuPreferences: MenuPreferencesState;
};

/** Builds the English paragraph appended to dietary_comment when arrival airport is set. */
export function formatFlightLegContextForPrompt(input: {
  departureDateTime: string;
  departureLabel: string;
  arrivalLabel: string;
}): string {
  return `Flight planning: Departure ${input.departureDateTime} from ${input.departureLabel}. Arrival: ${input.arrivalLabel}. Estimate reasonable block time and inflight meal service count (e.g. breakfast vs lunch vs dinner vs snack) for this leg; scale suggestions accordingly.`;
}

function formatPersonConstraints(
  allergyIds: string[],
  dietIds: string[],
  allergyOther: string,
  dietOther: string,
): string {
  const parts: string[] = [];
  const a = labelsFromIds(allergyIds.filter((id) => id !== "other"), "allergy");
  if (a) parts.push(`allergies/avoid: ${a}`);
  if (allergyIds.includes("other") && allergyOther.trim()) {
    parts.push(`other allergies: ${allergyOther.trim()}`);
  }
  const d = labelsFromIds(dietIds.filter((id) => id !== "other"), "diet");
  if (d) parts.push(`dietary requirements: ${d}`);
  if (dietIds.includes("other") && dietOther.trim()) {
    parts.push(`other dietary: ${dietOther.trim()}`);
  }
  return parts.join("; ");
}

/** Lower `priority` values are kept longer when trimming (0 = most important). */
function trimBlocksToMax(blocks: { text: string; priority: number }[], max: number): string {
  const usable = blocks.filter((b) => b.text.trim());
  if (usable.length === 0) return "";

  const joinSorted = (list: typeof usable) =>
    [...list].sort((a, b) => a.priority - b.priority).map((b) => b.text.trim()).join("\n\n");

  const current = [...usable];
  let text = joinSorted(current);
  if (text.length <= max) return text;

  while (text.length > max && current.length > 1) {
    current.sort((a, b) => b.priority - a.priority);
    current.pop();
    text = joinSorted(current);
  }

  if (text.length <= max) return text;

  const single = joinSorted(current);
  if (single.length <= max) return single;
  return `${single.slice(0, Math.max(0, max - 1))}…`;
}

export function buildDietaryCommentForWizard(input: BuildWizardDietaryInput): string {
  const blocks: { text: string; priority: number }[] = [];

  const leg = input.flightLegContext?.trim();
  if (leg) blocks.push({ text: leg, priority: 0 });

  const p = input.preflightNotes.trim();
  if (p) blocks.push({ text: `Order setup notes: ${p}`, priority: 0 });

  const groupBits = formatPersonConstraints(
    input.groupAllergyIds,
    input.groupDietIds,
    input.groupAllergyOther,
    input.groupDietOther,
  );
  const gp = input.groupPreferences.trim();
  if (groupBits || gp) {
    const body = [groupBits ? `General (group / remaining passengers): ${groupBits}` : "", gp ? `Group preferences: ${gp}` : ""]
      .filter(Boolean)
      .join("\n");
    blocks.push({ text: body, priority: 1 });
  }

  for (let i = 0; i < input.specialPassengers.length; i++) {
    const sp = input.specialPassengers[i];
    const adults =
      typeof sp.adultCount === "number"
        ? Math.max(0, sp.adultCount)
        : sp.passengerKind === "child"
          ? 0
          : 1;
    const children =
      typeof sp.childCount === "number"
        ? Math.max(0, sp.childCount)
        : sp.passengerKind === "child"
          ? 1
          : 0;
    if (adults + children === 0) continue;

    const name = sp.label.trim() || `group ${i + 1}`;
    const countBits = [
      adults > 0 ? `${adults} adult(s)` : null,
      children > 0 ? `${children} child(ren)` : null,
    ].filter(Boolean);
    const countLabel = countBits.join(", ");
    const c = formatPersonConstraints(sp.allergyIds, sp.dietIds, sp.allergyOther, sp.dietOther);
    const n = sp.notes.trim();
    const line = [`Dietary group (${name}) — ${countLabel}: ${c || "(no chip selections)"}`]
      .concat(n ? [`Notes: ${n}`] : [])
      .join(" ");
    blocks.push({ text: line, priority: 0 });
  }

  if (input.includeCrewMeals) {
    blocks.push({
      text: "Include meals for the flight crew (align with passenger service style where it makes sense).",
      priority: 2,
    });
  }

  const mp = input.menuPreferences;
  const prefLines: string[] = [];
  if (mp.menuStyle) prefLines.push(`Menu style: ${MENU_STYLE_LABELS[mp.menuStyle]}`);
  if (mp.cuisine) prefLines.push(`Cuisine: ${CUISINE_LABELS[mp.cuisine]}`);
  if (mp.occasion) prefLines.push(`Occasion: ${OCCASION_LABELS[mp.occasion]}`);
  if (mp.service) prefLines.push(`Service: ${SERVICE_LABELS[mp.service]}`);
  if (prefLines.length) {
    blocks.push({ text: `Menu preferences:\n${prefLines.join("\n")}`, priority: 3 });
  }

  return trimBlocksToMax(blocks, AI_ORDER_DIETARY_MAX_CHARS);
}

export function newSpecialPassengerDraft(): SpecialPassengerDraft {
  return {
    id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `sp-${Date.now()}`,
    label: "",
    adultCount: 1,
    childCount: 0,
    allergyIds: [],
    dietIds: [],
    allergyOther: "",
    dietOther: "",
    notes: "",
  };
}

/** Ensures adult/child counts exist (migrates legacy passengerKind). */
export function normalizeSpecialPassengerDraft(sp: SpecialPassengerDraft): SpecialPassengerDraft {
  const hasCounts =
    typeof sp.adultCount === "number" && typeof sp.childCount === "number";
  if (hasCounts) {
    return {
      ...sp,
      adultCount: Math.max(0, Math.min(500, Math.floor(sp.adultCount))),
      childCount: Math.max(0, Math.min(500, Math.floor(sp.childCount))),
    };
  }
  const legacyAdult = sp.passengerKind === "child" ? 0 : 1;
  const legacyChild = sp.passengerKind === "child" ? 1 : 0;
  return {
    ...sp,
    adultCount: legacyAdult,
    childCount: legacyChild,
  };
}

export function defaultMenuPreferencesState(): MenuPreferencesState {
  return {
    menuStyle: "classic_refined",
    cuisine: "mix",
    occasion: "none",
    service: "ready_to_serve",
  };
}

const LEGACY_SERVICE_MAP: Record<string, PassengerServiceStyleId> = {
  individual_trays: "ready_to_serve",
  shared_platters: "family_style",
};

/** Merges partial menu preferences and maps legacy `service` values from older drafts. */
export function normalizeMenuPreferencesState(partial?: Partial<MenuPreferencesState> | null): MenuPreferencesState {
  const base = defaultMenuPreferencesState();
  if (!partial || typeof partial !== "object") return base;
  const raw = partial.service as string | null | undefined;
  let service: MenuPreferencesState["service"] = base.service;
  if (raw && LEGACY_SERVICE_MAP[raw]) {
    service = LEGACY_SERVICE_MAP[raw];
  } else if (raw && PASSENGER_SERVICE_STYLE_OPTIONS.some((o) => o.id === raw)) {
    service = raw as PassengerServiceStyleId;
  }
  return {
    ...base,
    ...partial,
    service,
  };
}
